import { ApolloLink, Observable, type Operation, type FetchResult } from '@apollo/client/core';
import { print } from 'graphql';
import { createClient, type Client, type ClientOptions } from 'graphql-sse';

/**
 * Apollo Link that routes GraphQL operations over the graphql-sse protocol.
 * Adapted from The Guild's official recipe
 * (https://the-guild.dev/graphql/sse/recipes) since @apollo/client itself has
 * no built-in SSE link. Used only for `subscription` operations — queries and
 * mutations continue to flow through the HTTP link in graphql.ts via
 * apollo-client's `split()`.
 *
 * HotChocolate 13 exposes the same /graphql endpoint for both HTTP and SSE
 * transport — the client differentiates by sending `Accept: text/event-stream`
 * (which graphql-sse does automatically). No separate URL, no separate
 * WebSocket upgrade, one connection multiplexes cleanly under HTTP/2.
 *
 * Auth + operation headers are captured from each Operation's context.headers
 * (populated by the existing setContext links) and forwarded on every SSE
 * request so subscriptions inherit the caller's identity.
 */
export class SSELink extends ApolloLink {
    private readonly client: Client;

    constructor(options: ClientOptions) {
        super();
        this.client = createClient(options);
    }

    request(operation: Operation): Observable<FetchResult> {
        return new Observable(sink => {
            return this.client.subscribe<FetchResult>(
                { ...operation, query: print(operation.query) },
                {
                    next: sink.next.bind(sink),
                    complete: sink.complete.bind(sink),
                    error: sink.error.bind(sink),
                },
            );
        });
    }
}
